/**
 * np-product-zoom.js — full-screen image viewer for the product page
 *
 * Open:  tap/click a product image (.np-product-slide__image--zoomable) or the "+" button
 * Zoom:  tap/click the image to zoom in (ZOOM x); move the mouse or drag to look around;
 *        tap/click again to zoom out
 * Move:  numbered dots / arrows / swipe / ← → keys (same look as the product slider)
 * Close: × button / Esc
 */

(function () {
  var ZOOM = 2.5;
  var TAP_MAX = 10; // px: more movement than this is a drag/swipe, not a tap
  var SWIPE_MIN = 50; // px

  var sources = Array.prototype.slice.call(document.querySelectorAll('.np-product-slide__image--zoomable'));
  if (!sources.length) return;

  var images = sources.map(function (el) {
    return { src: el.dataset.zoomSrc, alt: el.dataset.zoomAlt || '' };
  });

  function duration() {
    // same speed as the product image slider
    var v = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--slide-duration'));
    return (v || 0.65) * 1000;
  }

  // ─── markup ───
  var root = document.createElement('div');
  root.className = 'np-zoom';
  root.setAttribute('role', 'dialog');
  root.setAttribute('aria-modal', 'true');
  root.setAttribute('aria-label', 'Product image viewer');
  root.hidden = true;

  var dotsHtml = '';
  if (images.length > 1) {
    dotsHtml =
      '<nav class="np-product-slider__dots np-zoom__dots" aria-label="Product image navigation">' +
      '<div class="np-product-slider__dot-list">' +
      images.map(function (_, i) {
        return '<button type="button" class="np-product-slider__dot" data-index="' + i + '" aria-label="View image ' + (i + 1) + '">' + (i + 1) + '</button>';
      }).join('') +
      '</div>' +
      '<div class="np-product-slider__arrows">' +
      '<button type="button" class="np-product-slider__arrow np-product-slider__arrow--prev" aria-label="Previous image"></button>' +
      '<button type="button" class="np-product-slider__arrow np-product-slider__arrow--next" aria-label="Next image"></button>' +
      '</div>' +
      '</nav>';
  }

  root.innerHTML =
    '<div class="np-zoom__bar"><button type="button" class="np-zoom__close" aria-label="Close"></button></div>' +
    '<div class="np-zoom__stage"></div>' +
    dotsHtml;
  document.body.appendChild(root);

  var stage = root.querySelector('.np-zoom__stage');
  var closeBtn = root.querySelector('.np-zoom__close');
  var dots = Array.prototype.slice.call(root.querySelectorAll('.np-product-slider__dot'));

  var current = 0;
  var img = null;
  var zoomed = false;
  var ox = 50;
  var oy = 50;
  var animating = false;
  var lastFocus = null;

  function makeImg(i) {
    var el = document.createElement('img');
    el.className = 'np-zoom__img';
    el.src = images[i].src;
    el.alt = images[i].alt;
    el.draggable = false;
    return el;
  }

  function setOrigin() {
    if (img) img.style.transformOrigin = ox + '% ' + oy + '%';
  }

  function setZoom(on) {
    zoomed = on;
    root.classList.toggle('is-zoomed', on);
    if (img) img.style.transform = on ? 'scale(' + ZOOM + ')' : '';
  }

  function updateDots() {
    dots.forEach(function (d, i) {
      d.classList.toggle('is-active', i === current);
      d.setAttribute('aria-current', i === current ? 'true' : 'false');
    });
  }

  function show(i, direction) {
    var next = makeImg(i);
    var old = img;
    current = i;
    img = next;
    setZoom(false);
    ox = oy = 50;
    setOrigin();
    stage.appendChild(next);
    updateDots();

    if (!old) return;
    if (!direction || !next.animate || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      old.remove();
      return;
    }

    // same slide as the product slider: forward enters from the right
    animating = true;
    var opts = { duration: duration(), easing: 'ease' };
    var outTo = direction === 'forward' ? '-100%' : '100%';
    var inFrom = direction === 'forward' ? '100%' : '-100%';
    old.animate([{ transform: 'translateX(0)' }, { transform: 'translateX(' + outTo + ')' }], opts);
    var anim = next.animate([{ transform: 'translateX(' + inFrom + ')' }, { transform: 'translateX(0)' }], opts);
    anim.onfinish = anim.oncancel = function () {
      old.remove();
      animating = false;
    };
  }

  function step(delta) {
    if (animating || images.length < 2) return;
    var n = images.length;
    show((current + delta + n) % n, delta > 0 ? 'forward' : 'backward');
  }

  function open(i) {
    lastFocus = document.activeElement;
    stage.innerHTML = '';
    img = null;
    show(i);
    root.hidden = false;
    document.documentElement.classList.add('np-zoom-open');
    closeBtn.focus();
  }

  function close() {
    root.hidden = true;
    document.documentElement.classList.remove('np-zoom-open');
    setZoom(false);
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  // ─── open from the product slider ───
  sources.forEach(function (el, i) {
    el.addEventListener('click', function () { open(i); });
  });

  // "+" button: opens the image currently shown in the slider
  var zoomBtn = document.querySelector('.np-product-slider__zoom');
  if (zoomBtn) {
    zoomBtn.addEventListener('click', function () {
      var slides = Array.prototype.slice.call(document.querySelectorAll('.np-product-slide'));
      var active = slides.findIndex(function (s) { return s.classList.contains('is-active'); });
      open(Math.max(0, active));
    });
  }

  // ─── controls ───
  closeBtn.addEventListener('click', close);

  dots.forEach(function (d, i) {
    d.addEventListener('click', function () {
      if (i === current || animating) return;
      show(i, i > current ? 'forward' : 'backward');
    });
  });

  var prevBtn = root.querySelector('.np-product-slider__arrow--prev');
  var nextBtn = root.querySelector('.np-product-slider__arrow--next');
  if (prevBtn) prevBtn.addEventListener('click', function () { step(-1); });
  if (nextBtn) nextBtn.addEventListener('click', function () { step(1); });

  document.addEventListener('keydown', function (e) {
    if (root.hidden) return;
    if (e.key === 'Escape') close();
    else if (e.key === 'ArrowRight') step(1);
    else if (e.key === 'ArrowLeft') step(-1);
  });

  // ─── tap to zoom, drag/mouse to look around, swipe to change image ───
  var down = null; // { x, y, ox, oy, moved, touch }

  stage.addEventListener('pointerdown', function (e) {
    if (!e.isPrimary || animating) return;
    down = { x: e.clientX, y: e.clientY, ox: ox, oy: oy, moved: false, touch: e.pointerType !== 'mouse' };
    if (down.touch) stage.setPointerCapture(e.pointerId);
  });

  stage.addEventListener('pointermove', function (e) {
    if (!e.isPrimary) return;
    var rect = stage.getBoundingClientRect();

    // mouse while zoomed: the image follows the pointer
    if (e.pointerType === 'mouse') {
      if (zoomed) {
        ox = Math.min(100, Math.max(0, ((e.clientX - rect.left) / rect.width) * 100));
        oy = Math.min(100, Math.max(0, ((e.clientY - rect.top) / rect.height) * 100));
        setOrigin();
      }
      return;
    }

    if (!down) return;
    var dx = e.clientX - down.x;
    var dy = e.clientY - down.y;
    if (Math.abs(dx) > TAP_MAX || Math.abs(dy) > TAP_MAX) down.moved = true;

    // finger while zoomed: drag the image
    if (zoomed) {
      ox = Math.min(100, Math.max(0, down.ox - (dx / rect.width) * 100 / (ZOOM - 1)));
      oy = Math.min(100, Math.max(0, down.oy - (dy / rect.height) * 100 / (ZOOM - 1)));
      setOrigin();
    }
  });

  stage.addEventListener('pointerup', function (e) {
    if (!down || !e.isPrimary) return;
    var d = down;
    down = null;
    var dx = e.clientX - d.x;
    var dy = e.clientY - d.y;

    if (!d.moved && Math.abs(dx) <= TAP_MAX && Math.abs(dy) <= TAP_MAX) {
      // tap/click: zoom in at that point, or zoom out
      if (!zoomed) {
        var rect = stage.getBoundingClientRect();
        ox = ((e.clientX - rect.left) / rect.width) * 100;
        oy = ((e.clientY - rect.top) / rect.height) * 100;
        setOrigin();
      }
      setZoom(!zoomed);
      return;
    }

    if (!zoomed && d.touch && Math.abs(dx) >= SWIPE_MIN && Math.abs(dx) > Math.abs(dy)) {
      step(dx < 0 ? 1 : -1);
    }
  });

  stage.addEventListener('pointercancel', function () { down = null; });
})();
